import type { CSSProperties, KeyboardEvent } from "react";
import { BookOpenText, Clock3 } from "lucide-react";
import { IconTile, ListItem, cn } from "@playbookai/ui";
import {
  SECTION_LABELS,
  getKnowledgeDomain,
  type KnowledgeArticle,
} from "../content/index.js";
import { knowledgeTermPattern, type KnowledgeSearchResult } from "../search.js";

function HighlightedText({
  text,
  terms,
}: {
  readonly text: string;
  readonly terms: readonly string[];
}): JSX.Element {
  if (terms.length === 0) return <>{text}</>;
  const expression = knowledgeTermPattern(terms, true);
  const termSet = new Set(terms.map((term) => term.toLocaleLowerCase()));
  return (
    <>
      {text.split(expression).map((part, index) =>
        termSet.has(part.toLocaleLowerCase()) ? (
          <mark
            key={`${part}-${index}`}
            className="rounded-sm bg-pb-teal-soft px-0.5 text-pb-text"
          >
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

export function KnowledgeSearchResultRow({
  result,
  onOpen,
  onMove,
  className,
  style,
}: {
  readonly result: KnowledgeSearchResult;
  readonly onOpen: (article: KnowledgeArticle) => void;
  readonly onMove: (direction: -1 | 1) => void;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  const { article, excerpt, highlightTerms } = result;
  const domain = getKnowledgeDomain(article.domainId);
  return (
    // The icon and the three-line text block stay top-aligned inside one
    // child, rather than through ListItem's `leading` slot (whose row is
    // items-center) — that would re-centre the icon against the full block
    // instead of its title line, a look no T01 row approves.
    <ListItem
      onSelect={() => onOpen(article)}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        event.preventDefault();
        onMove(event.key === "ArrowDown" ? 1 : -1);
      }}
      className={cn("px-2 py-3.5", className)}
      style={style}
    >
      <div className="flex w-full items-start gap-3">
        <IconTile size="sm" className="mt-0.5">
          <BookOpenText aria-hidden="true" />
        </IconTile>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-pb-text">
            <HighlightedText text={article.title} terms={highlightTerms} />
          </span>
          <span className="mt-1 block text-xs leading-5 text-pb-text-secondary">
            <HighlightedText text={excerpt} terms={highlightTerms} />
          </span>
          <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-pb-text-muted">
            <span>{domain?.name}</span>
            <span>{SECTION_LABELS[article.section]}</span>
            <span className="inline-flex items-center gap-1">
              <Clock3 aria-hidden="true" className="h-3 w-3" />
              {article.readMinutes} min read
            </span>
          </span>
        </span>
      </div>
    </ListItem>
  );
}
